<template>
    <div>
        <h1>SP Availability</h1>
        <p><small>Pick a partner and scenario to see when standardized patients actually have open time, and which requests are still open.</small></p>
    </div>
    <div class="card shadow-sm mb-3">
        <div class="card-body">
            <select class="border-0 text-secondary me-2" v-model="query_partner" @change="onPartnerChange">
                <option :value="null">Select Partner</option>
                <option v-for="partner in partners" v-bind:key="String(partner.id)" :value="partner.id">{{partner.short_name}}</option>
            </select>
            <select class="border-0 text-secondary me-2" v-model="query_scenario" :disabled="!scenarios.length">
                <option :value="null">Select Scenario</option>
                <option v-for="scenario in scenarios" v-bind:key="String(scenario.id)" :value="scenario.id">{{scenario.patient_name || scenario.title}}</option>
            </select>
            <button class="btn btn-lg btn-primary" :disabled="!query_partner || !query_scenario" @click="generate()">Generate</button>
            <div v-if="query_partner && scenarios_loaded && !scenarios.length" class="text-muted mt-2">There are no available scenarios for this partner.</div>
        </div>
    </div>

    <div v-if="has_error" class="alert alert-danger">{{error_message}}</div>

    <div v-if="result">
        <div class="card shadow-sm mb-3">
            <div class="card-body">
                <h5>{{result.program.partner_name}} &mdash; {{result.program.scenario_title}}</h5>
                <div>SPs assigned to this program: <strong>{{result.program.sp_count}}</strong></div>
                <div>Open pending requests: <strong>{{result.open_request_count}}</strong></div>
                <div>Valid scheduling range: <strong>{{result.valid_range.effective_start || 'n/a'}} &ndash; {{result.valid_range.effective_end || 'n/a'}}</strong>
                    <span v-if="result.valid_range.partner_date_constrained"> (limited by partner scheduling dates)</span>
                </div>
                <div>All times shown in your time zone: <strong>{{result.timezone}}</strong></div>
            </div>
        </div>

        <div class="card shadow-sm mb-3">
            <div class="card-body">
                <h5>Weekly Availability Pattern</h5>
                <div v-if="!rollupColumns.length" class="text-muted">{{noDataReason}}</div>
                <template v-else>
                    <div class="table-responsive">
                        <table class="table table-bordered text-center availability-heatmap">
                            <thead class="thead-dark">
                                <tr>
                                    <th></th>
                                    <th v-for="weekday in weekdayOrder" v-bind:key="weekday">{{weekday}}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="column in rollupColumns" v-bind:key="column.hour + '_' + column.minute">
                                    <th class="text-start">{{formatTime(column.hour, column.minute)}}</th>
                                    <td v-for="weekday in weekdayOrder" v-bind:key="weekday"
                                        :style="cellStyle(weekday, column)">
                                        {{rollupValue(weekday, column)}}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="text-muted mt-2"><small>Darker cells = more standardized patients available on average. Each cell averages that exact half-hour slot across the valid range, so fractional values (e.g. 1.5) are expected &mdash; they mean availability varies across the weeks that share that slot, not that "half an SP" is free.</small></div>
                    <div class="mt-3" v-if="generalRecommendedWindows.length">
                        <div><strong>General recommended windows</strong></div>
                        <ul class="mb-0">
                            <li v-for="(window,ind) in generalRecommendedWindows" v-bind:key="ind">
                                {{window.weekday}} between {{formatTime(window.startHour, window.startMinute)}} - {{formatTime(window.endHour, window.endMinute)}}
                            </li>
                        </ul>
                    </div>
                </template>
            </div>
        </div>

        <div class="card shadow-sm mb-3">
            <div class="card-body">
                <h5>Recommended Redirect Windows</h5>
                <div v-if="!result.recommended_windows.length" class="text-muted">{{noDataReason}}</div>
                <div v-else class="table-responsive">
                    <table class="table table-striped">
                        <thead class="thead-dark">
                            <tr>
                                <th>Date</th>
                                <th>Weekday</th>
                                <th>Time</th>
                                <th>Available SPs</th>
                                <th>Current Demand</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(slot,ind) in result.recommended_windows" v-bind:key="ind">
                                <td>{{slot.date}}</td>
                                <td>{{slot.weekday}}</td>
                                <td>{{moment.parseZone(slot.start_time).format('h:mm a')}}</td>
                                <td>{{slot.available_sp_count}}</td>
                                <td>{{slot.requested_demand_count}}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="card shadow-sm mb-3">
            <div class="card-body">
                <h5>Open Requests</h5>
                <div v-if="!result.open_requests.length" class="text-muted">No open requests for this program.</div>
                <div v-else class="table-responsive">
                    <table class="table table-striped">
                        <thead class="thead-dark">
                            <tr>
                                <th>Learner</th>
                                <th>Days Pending</th>
                                <th>Originally Requested (your time)</th>
                                <th>Requester's Local Time</th>
                                <th>SP Available?</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="req in result.open_requests" v-bind:key="req.request_id">
                                <td>
                                    <div>{{req.user ? req.user.name : 'Unknown'}}</div>
                                    <div class="text-muted"><small>{{req.user ? req.user.email : ''}}</small></div>
                                </td>
                                <td>{{formatDaysPending(req.days_pending)}}</td>
                                <td>
                                    <div v-for="(date,ind) in req.originally_requested_dates" v-bind:key="ind">
                                        {{moment.parseZone(date.start_time_your_tz).format('dddd MM/DD/YY h:mm a')}}
                                        <span v-if="date.outside_current_partner_range" class="badge bg-danger ms-1">outside current range</span>
                                    </div>
                                </td>
                                <td>
                                    <div v-for="(date,ind) in req.originally_requested_dates" v-bind:key="ind">
                                        {{moment.parseZone(date.start_time).format('dddd MM/DD/YY h:mm a')}} ({{date.timezone}})
                                    </div>
                                </td>
                                <td>
                                    <div v-for="(date,ind) in req.originally_requested_dates" v-bind:key="ind">
                                        <span v-if="date.sp_available_count > 0" class="badge bg-success">Yes ({{date.sp_available_count}})</span>
                                        <span v-else class="badge bg-secondary">No</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>

    <loading :active="loading" :is-full-page="true"></loading>
</template>

<script lang="ts">
    import {computed, defineComponent, ref} from 'vue';
    import {MutationTypes} from "@/store/modules/ux/mutations";
    import {NotificationIcon, NotificationColor} from "@/interfaces/notification";
    import {store} from '@/store';
    import moment from 'moment';
    import Loading from 'vue3-loading-overlay';
    import Partner from '@/models/partner';
    import ProgramAvailability from '@/models/program-availability';

    // Sequential blue ramp (light -> dark), documented steps 100-700 from the dataviz skill's palette reference.
    const SEQUENTIAL_STEPS = ['#cde2fb','#b7d3f6','#9ec5f4','#86b6ef','#6da7ec','#5598e7','#3987e5','#2a78d6','#256abf','#1c5cab','#184f95','#104281','#0d366b'];
    const WEEKDAY_ORDER = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

    export default defineComponent({
        components: {Loading},
        mounted(){
            this.loadPartners();
        },
        setup() {
            const loading = ref<boolean>(false);
            const has_error = ref<boolean>(false);
            const error_message = ref<string>('');
            const partners = ref<Array<any>>([]);
            const scenarios = ref<Array<any>>([]);
            const scenarios_loaded = ref<boolean>(false);
            const query_partner = ref<number|null>(null);
            const query_scenario = ref<number|null>(null);
            const result = ref<any>(null);

            const loadPartners = () => {
                // store.getters.partners is only the current user's own partner assignments
                // (User::partners(), a belongsToMany through user_partners), and Partner.list()
                // (Partner::index()) is scoped to the current brand's IS_SIM context -- this page
                // needs every partner across both brands, so it uses a dedicated endpoint.
                ProgramAvailability.listPartners().then((res:any)=>{
                    partners.value = res.data.partners.slice().sort((a:any,b:any)=> a.short_name.localeCompare(b.short_name));
                },(err:any)=>{
                    store.commit(MutationTypes.addSystemNotification, {title:'Error loading partners.',content:err.response.data.message,icon:NotificationIcon.ERROR,color:NotificationColor.RED});
                });
            }

            const onPartnerChange = () => {
                query_scenario.value = null;
                scenarios.value = [];
                scenarios_loaded.value = false;
                result.value = null;
                if(query_partner.value){
                    Partner.scenarios(query_partner.value).then((res:any)=>{
                        scenarios.value = res.data.slice().sort((a:any,b:any)=> (a.patient_name || a.title || '').localeCompare(b.patient_name || b.title || ''));
                        scenarios_loaded.value = true;
                    },(err:any)=>{
                        scenarios_loaded.value = true;
                        store.commit(MutationTypes.addSystemNotification, {title:'Error loading scenarios.',content:err.response.data.message,icon:NotificationIcon.ERROR,color:NotificationColor.RED});
                    });
                }
            }

            const generate = () => {
                if(!query_partner.value || !query_scenario.value) return;
                loading.value = true;
                has_error.value = false;
                error_message.value = '';
                ProgramAvailability.getRedirectWindows({
                    partner_id: query_partner.value,
                    scenario_id: query_scenario.value
                }).then((res:any)=>{
                    result.value = res.data;
                    loading.value = false;
                },(err:any)=>{
                    loading.value = false;
                    has_error.value = true;
                    error_message.value = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Error retrieving availability data.';
                    store.commit(MutationTypes.addSystemNotification, {title:'Error retrieving availability data.',content:error_message.value,icon:NotificationIcon.ERROR,color:NotificationColor.RED});
                });
            }

            const rollupColumns = computed(()=>{
                if(!result.value) return [];
                const seen = new Map<number,{hour:number;minute:number}>();
                result.value.weekday_hour_rollup.forEach((row:any)=>{
                    const key = row.hour * 60 + row.minute;
                    if(!seen.has(key)) seen.set(key, {hour: row.hour, minute: row.minute});
                });
                return Array.from(seen.keys()).sort((a,b)=>a-b).map((key)=> seen.get(key) as {hour:number;minute:number});
            });

            const rollupMax = computed(()=>{
                if(!result.value) return 0;
                return result.value.weekday_hour_rollup.reduce((max:number,row:any)=> Math.max(max,row.avg_available_sp_count), 0);
            });

            const rollupKey = (weekday:string, column:{hour:number;minute:number}) => weekday + '|' + column.hour + '|' + column.minute;

            const rollupLookup = computed(()=>{
                const lookup:Record<string,number> = {};
                if(result.value){
                    result.value.weekday_hour_rollup.forEach((row:any)=>{
                        lookup[row.weekday + '|' + row.hour + '|' + row.minute] = row.avg_available_sp_count;
                    });
                }
                return lookup;
            });

            const rollupValue = (weekday:string, column:{hour:number;minute:number}) => {
                const value = rollupLookup.value[rollupKey(weekday, column)];
                return typeof value === 'number' ? value : '-';
            }

            const cellStyle = (weekday:string, column:{hour:number;minute:number}) => {
                const value = rollupLookup.value[rollupKey(weekday, column)];
                if(typeof value !== 'number') return {};
                const max = rollupMax.value;
                const ratio = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
                const index = Math.round(ratio * (SEQUENTIAL_STEPS.length - 1));
                return {
                    backgroundColor: SEQUENTIAL_STEPS[index],
                    color: index >= 8 ? '#fcfcfb' : '#212529'
                };
            }

            const formatTime = (hour:number, minute:number) => moment().hour(hour).minute(minute).format('h:mm a');

            const noDataReason = computed(()=>{
                if(!result.value) return '';
                if(!result.value.program.sp_count){
                    return 'No standardized patients are currently assigned to this scenario.';
                }
                return 'No open windows found in the current valid scheduling range.';
            });

            // Half-hour slots whose avg_available_sp_count is at least this fraction of the
            // observed max are considered "good" -- merged into contiguous per-weekday ranges below.
            const GOOD_AVAILABILITY_RATIO = 0.66;
            // Below this width a range isn't useful to hand a participant -- drop it rather than
            // show a misleading single-slot window (e.g. "12pm - 1pm" reads as if 1pm is good too).
            const MIN_RECOMMENDATION_MINUTES = 180;

            const pushWindowIfLongEnough = (
                windows:Array<{weekday:string;startHour:number;startMinute:number;endHour:number;endMinute:number}>,
                weekday:string,
                columns:Array<{hour:number;minute:number}>,
                startIdx:number,
                endIdx:number
            ) => {
                const startColumn = columns[startIdx];
                const endColumn = columns[endIdx];
                const startMinutes = startColumn.hour * 60 + startColumn.minute;
                const endMinutes = endColumn.hour * 60 + endColumn.minute + 30; // end of the last good half-hour slot
                if(endMinutes - startMinutes < MIN_RECOMMENDATION_MINUTES) return;
                windows.push({
                    weekday,
                    startHour: Math.floor(startMinutes / 60),
                    startMinute: startMinutes % 60,
                    endHour: Math.floor(endMinutes / 60) % 24,
                    endMinute: endMinutes % 60,
                });
            }

            const generalRecommendedWindows = computed(()=>{
                const windows:Array<{weekday:string;startHour:number;startMinute:number;endHour:number;endMinute:number}> = [];
                const columns = rollupColumns.value;
                const max = rollupMax.value;
                if(!columns.length || max <= 0) return windows;
                const threshold = max * GOOD_AVAILABILITY_RATIO;

                WEEKDAY_ORDER.forEach((weekday)=>{
                    let runStartIndex:number|null = null;

                    columns.forEach((column, idx)=>{
                        const value = rollupLookup.value[rollupKey(weekday, column)];
                        const isGood = typeof value === 'number' && value >= threshold;
                        const previousColumn = idx > 0 ? columns[idx - 1] : null;
                        const currentMinutes = column.hour * 60 + column.minute;
                        const previousMinutes = previousColumn ? previousColumn.hour * 60 + previousColumn.minute : null;
                        const brokeSequence = previousMinutes !== null && currentMinutes !== previousMinutes + 30;

                        if(brokeSequence && runStartIndex !== null){
                            pushWindowIfLongEnough(windows, weekday, columns, runStartIndex, idx - 1);
                            runStartIndex = null;
                        }
                        if(isGood){
                            if(runStartIndex === null) runStartIndex = idx;
                        }else if(runStartIndex !== null){
                            pushWindowIfLongEnough(windows, weekday, columns, runStartIndex, idx - 1);
                            runStartIndex = null;
                        }
                    });
                    if(runStartIndex !== null){
                        pushWindowIfLongEnough(windows, weekday, columns, runStartIndex, columns.length - 1);
                    }
                });

                return windows;
            });

            const formatDaysPending = (daysFloat:number) => {
                const totalHours = Math.round(daysFloat * 24);
                const days = Math.floor(totalHours / 24);
                const hours = totalHours % 24;
                const parts = [];
                if(days) parts.push(days + (days === 1 ? ' day' : ' days'));
                if(hours || !days) parts.push(hours + (hours === 1 ? ' hour' : ' hours'));
                return parts.join(' ');
            }

            return {
                cellStyle,
                error_message,
                formatDaysPending,
                formatTime,
                generalRecommendedWindows,
                generate,
                has_error,
                loadPartners,
                loading,
                moment,
                noDataReason,
                onPartnerChange,
                partners,
                query_partner,
                query_scenario,
                result,
                rollupColumns,
                rollupValue,
                scenarios,
                scenarios_loaded,
                weekdayOrder: WEEKDAY_ORDER,
            };
        },
    });
</script>

<style lang="scss">
    .availability-heatmap td {
        min-width: 48px;
    }
</style>
